import { useState } from 'react';
import { auth, db } from '../firebase-config';
import { 
  signInWithEmailAndPassword, 
  GoogleAuthProvider, 
  signInWithPopup, 
  sendPasswordResetEmail 
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import "../style/Global.css";
import "../style/Login.css";

function Login({ setIsAuth, setRole }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [showRolePopup, setShowRolePopup] = useState(false);
    const [tempGoogleUser, setTempGoogleUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState('Student');
    const navigate = useNavigate();
    const provider = new GoogleAuthProvider();

    provider.setCustomParameters({
        prompt: 'select_account'
    });

    const signIn = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
    
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            
            if (!userCredential.user.emailVerified) {
                await auth.signOut();
                setError("Please verify your email before logging in.");
                setLoading(false);
                return;
            }
            
            await handleUserLogin(userCredential.user);
        } catch (error) {
            setError(error.code === 'auth/invalid-credential' 
                ? "Invalid email or password. Please try again."
                : "Login failed. Please try again.");
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        setShowForgotPassword(true);
        setError('');
        setResetSuccess('');
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setResetLoading(true);
        setError('');
        setResetSuccess('');
        
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(`Password reset email sent to ${resetEmail}. Please check your inbox.`);
            setResetEmail('');
        } catch (error) {
            setError(error.code === 'auth/user-not-found'
                ? "No account found with this email address."
                : "Failed to send reset email. Please try again.");
        } finally {
            setResetLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        setError("");
        setGoogleLoading(true);
        
        try {
            await auth.signOut();
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            
            const userDoc = await getDoc(doc(db, "Users", user.uid));
            
            if (!userDoc.exists()) {
                setTempGoogleUser({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || ''
                });
                setShowRolePopup(true);
            } else {
                await handleUserLogin(user);
            }
        } catch (error) {
            if (error.code !== 'auth/popup-closed-by-user' && 
                error.code !== 'auth/cancelled-popup-request') {
                setError("Failed to sign in with Google. Please try again.");
            }
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleRoleSelection = async () => {
        try {
            setShowRolePopup(false);
            setGoogleLoading(true);
            
            const nameParts = tempGoogleUser.displayName.split(' ');
            const firstName = nameParts[0] || 'User';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            await setDoc(doc(db, "Users", tempGoogleUser.uid), {
                email: tempGoogleUser.email,
                firstName,
                lastName,
                uid: tempGoogleUser.uid,
                role: selectedRole,
                emailVerified: true,
                createdAt: new Date().toISOString()
            });

            // Proceed with login
            sessionStorage.setItem("isAuth", "true");
            sessionStorage.setItem("role", selectedRole);
            setIsAuth(true);
            setRole(selectedRole);

            navigate(selectedRole === "Teacher" 
                ? '/TeacherDashboard' 
                : '/StudentDashboard');
        } catch (error) {
            setError("Failed to complete registration. Please try again.");
            setGoogleLoading(false);
        }
    };

    const handleUserLogin = async (user) => {
        try {
            const userDoc = await getDoc(doc(db, "Users", user.uid));
            
            if (userDoc.exists()) {
                const userData = userDoc.data();
                sessionStorage.setItem("isAuth", "true");
                sessionStorage.setItem("role", userData.role);
                setIsAuth(true);
                setRole(userData.role);

                navigate(userData.role === "Teacher" 
                    ? '/TeacherDashboard' 
                    : '/StudentDashboard');
            } else {
                setError("Account not properly registered. Please sign up again.");
            }
        } catch (error) {
            setError("An error occurred during login. Please try again.");
        } finally {
            setLoading(false);
            setGoogleLoading(false);
        }
    };

    return (
        <div className="loginPage">
            <div className="leftSide">
                <img src="/Images/Image2.png" alt="login/signup" />
            </div>
            <div className="rightSide">
                {!showForgotPassword ? (
                    <>
                        <h2>Log In</h2>
                        {error && <p className="error">{error}</p>}
                        <form onSubmit={signIn}>
                            Email Address
                            <input
                                type="email"
                                placeholder="Enter your email here..."
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading || googleLoading}
                            />
                            Password
                            <input
                                type="password"
                                placeholder="Enter your password here..."
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading || googleLoading}
                            />
                            <button type="submit" disabled={loading || googleLoading}>
                                {loading ? 'Logging In...' : 'Log In'}
                            </button>
                        </form>
                        
                        <div className="oauth-section">
                            <p className="divider">OR</p>
                            <button 
                                className="google-btn"
                                onClick={signInWithGoogle}
                                disabled={loading || googleLoading}
                            >
                                <img 
                                    src="/Images/Google.png" 
                                    alt="Google logo" 
                                />
                                {googleLoading ? 'Connecting...' : 'Continue with Google'}
                            </button>
                        </div>
                        
                        <button 
                            className="forgot-password-btn" 
                            onClick={handleForgotPassword}
                            disabled={loading || googleLoading}
                        >
                            Forgot Password?
                        </button>
                        <button 
                            className="sign-up-btn" 
                            onClick={() => navigate('/SignUp')}
                            disabled={loading || googleLoading}
                        >
                            No account yet? Sign Up
                        </button>
                    </>
                ) : (
                    <>
                        <h2>Reset Password</h2>
                        {resetSuccess && <p className="success">{resetSuccess}</p>}
                        {error && <p className="error">{error}</p>}
                        <form onSubmit={handleResetPassword}>
                            <p>Enter your email address and we'll send you a link to reset your password.</p>
                            <input
                                type="email"
                                placeholder="Enter your email here..."
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                required
                                disabled={resetLoading}
                            />
                            <div className="reset-buttons">
                                <button 
                                    type="submit" 
                                    disabled={resetLoading}
                                    className={resetLoading ? 'loading' : ''}
                                >
                                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                                </button>
                                <button 
                                    type="button"
                                    className="back-to-login"
                                    onClick={() => setShowForgotPassword(false)}
                                    disabled={resetLoading}
                                >
                                    Back to Login
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>

            {/* Role Selection Modal */}
            {showRolePopup && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Select Your Role</h3>                        
                        <div className="role-options">
                            <label className={`role-option ${selectedRole === 'Student' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="role"
                                    value="Student"
                                    checked={selectedRole === 'Student'}
                                    onChange={() => setSelectedRole('Student')}
                                />
                                <div className="role-content">
                                    <h4>Student</h4>
                                </div>
                            </label>
                            
                            <label className={`role-option ${selectedRole === 'Teacher' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="role"
                                    value="Teacher"
                                    checked={selectedRole === 'Teacher'}
                                    onChange={() => setSelectedRole('Teacher')}
                                />
                                <div className="role-content">
                                    <h4>Teacher</h4>
                                </div>
                            </label>
                        </div>
                        
                        <div className="modal-buttons">
                            <button 
                                className="modal-confirm" 
                                onClick={handleRoleSelection}
                                disabled={googleLoading}
                            >
                                {googleLoading ? 'Setting Up...' : 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Login;